import React, { useState, useMemo } from 'react';
import { Send, Users, Search, CheckSquare, Square, AlertCircle, CheckCircle, Loader, Calendar } from 'lucide-react';
import { useAnnounceUsers, useSendAnnouncement } from '../../../hooks/useAnnounce';
import './ComposePage.css';

const ROLE_OPTIONS = ['All', 'admin', 'manager', 'user'];

function ComposePage() {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [familyFilter, setFamilyFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [sendAt, setSendAt] = useState('');
  const [result, setResult] = useState(null);

  const { data: users = [], isLoading, error: usersError } = useAnnounceUsers();
  const { mutateAsync: sendAnnouncement, isPending: isSending } = useSendAnnouncement();

  const families = useMemo(() => {
    const set = new Set(users.map(u => u.family).filter(Boolean));
    return ['All', ...Array.from(set).sort()];
  }, [users]);

  const filtered = useMemo(() => {
    return users.filter(u => {
      if (roleFilter !== 'All' && u.role !== roleFilter) return false;
      if (familyFilter && familyFilter !== 'All' && u.family !== familyFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(u.name || '').toLowerCase().includes(s) && !(u.email || '').toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [users, roleFilter, familyFilter, search]);

  const allFilteredSelected = filtered.length > 0 && filtered.every(u => selectedIds.has(u.id));

  function toggleUser(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllFiltered() {
    if (allFilteredSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filtered.forEach(u => next.delete(u.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filtered.forEach(u => next.add(u.id));
        return next;
      });
    }
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  async function handleSend() {
    if (selectedIds.size === 0 || !subject.trim() || !htmlBody.trim()) return;
    setResult(null);
    try {
      const data = await sendAnnouncement({
        recipientIds: Array.from(selectedIds),
        subject: subject.trim(),
        htmlBody: htmlBody.trim(),
        sendAt: scheduleEnabled && sendAt ? sendAt : null,
      });
      setResult({ type: 'success', data });
    } catch (err) {
      setResult({ type: 'error', message: err.message });
    }
  }

  const canSend = selectedIds.size > 0 && subject.trim() && htmlBody.trim() && !isSending;

  return (
    <div className="compose-page">
      <div className="compose-header">
        <h3>Compose Announcement</h3>
        <p className="compose-subtitle">Send an email to any group of users, independent of reservations or events.</p>
      </div>

      {result && result.type === 'success' && (
        <div className="compose-result compose-result--success">
          <CheckCircle size={18} />
          <span>
            {result.data.jobsCreated} email{result.data.jobsCreated !== 1 ? 's' : ''} queued successfully
            {result.data.skippedDuplicates > 0 && ` (${result.data.skippedDuplicates} skipped as duplicates)`}.
          </span>
        </div>
      )}
      {result && result.type === 'error' && (
        <div className="compose-result compose-result--error">
          <AlertCircle size={18} />
          <span>{result.message}</span>
        </div>
      )}

      <div className="compose-layout">
        {/* Left: Recipient Picker */}
        <div className="compose-recipients">
          <div className="compose-section-title">
            <Users size={15} />
            Recipients
            {selectedIds.size > 0 && (
              <span className="compose-badge">{selectedIds.size} selected</span>
            )}
          </div>

          <div className="compose-filters">
            <div className="compose-search-wrap">
              <Search size={14} className="compose-search-icon" />
              <input
                className="compose-search"
                type="text"
                placeholder="Search name or email..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select
              className="compose-select"
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value)}
            >
              {ROLE_OPTIONS.map(r => (
                <option key={r} value={r}>{r === 'All' ? 'All roles' : r}</option>
              ))}
            </select>
            {families.length > 1 && (
              <select
                className="compose-select"
                value={familyFilter}
                onChange={e => setFamilyFilter(e.target.value)}
              >
                {families.map(f => (
                  <option key={f} value={f}>{f === 'All' ? 'All families' : f}</option>
                ))}
              </select>
            )}
          </div>

          <div className="compose-list-header">
            <button className="compose-toggle-all" onClick={toggleAllFiltered}>
              {allFilteredSelected
                ? <CheckSquare size={15} />
                : <Square size={15} />}
              {allFilteredSelected ? 'Deselect all' : `Select all (${filtered.length})`}
            </button>
            {selectedIds.size > 0 && (
              <button className="compose-clear" onClick={clearSelection}>Clear</button>
            )}
          </div>

          <div className="compose-user-list">
            {isLoading && <div className="compose-loading"><Loader size={16} className="spin" /> Loading users...</div>}
            {usersError && <div className="compose-error-msg">Failed to load users</div>}
            {!isLoading && filtered.length === 0 && <div className="compose-empty">No users match filters</div>}
            {filtered.map(user => (
              <label key={user.id} className={`compose-user-row ${selectedIds.has(user.id) ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(user.id)}
                  onChange={() => toggleUser(user.id)}
                />
                <div className="compose-user-info">
                  <span className="compose-user-name">{user.name || '(unnamed)'}</span>
                  <span className="compose-user-email">{user.email}</span>
                </div>
                <div className="compose-user-meta">
                  {user.family && <span className="compose-tag compose-tag--family">{user.family}</span>}
                  <span className={`compose-tag compose-tag--role compose-tag--role-${user.role}`}>{user.role}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Right: Message */}
        <div className="compose-message">
          <div className="compose-section-title">
            <Send size={15} />
            Message
          </div>

          <label className="compose-label">Subject</label>
          <input
            className="compose-input"
            type="text"
            placeholder="Email subject line..."
            value={subject}
            onChange={e => setSubject(e.target.value)}
          />

          <label className="compose-label">Body (HTML)</label>
          <textarea
            className="compose-body"
            placeholder="<p>Your announcement here...</p>"
            value={htmlBody}
            onChange={e => setHtmlBody(e.target.value)}
            rows={12}
          />

          {htmlBody && (
            <details className="compose-preview-toggle">
              <summary>Preview rendered HTML</summary>
              <div className="compose-preview-frame-wrap">
                <iframe
                  className="compose-preview-frame"
                  srcDoc={htmlBody}
                  title="Email preview"
                  sandbox=""
                />
              </div>
            </details>
          )}

          <div className="compose-schedule">
            <label className="compose-schedule-toggle">
              <input
                type="checkbox"
                checked={scheduleEnabled}
                onChange={e => setScheduleEnabled(e.target.checked)}
              />
              <Calendar size={14} />
              Schedule for later
            </label>
            {scheduleEnabled && (
              <input
                className="compose-input compose-datetime"
                type="datetime-local"
                value={sendAt}
                onChange={e => setSendAt(e.target.value)}
              />
            )}
          </div>

          <button
            className="compose-send-btn"
            onClick={handleSend}
            disabled={!canSend}
          >
            {isSending
              ? <><Loader size={15} className="spin" /> Sending...</>
              : <><Send size={15} /> Send to {selectedIds.size} recipient{selectedIds.size !== 1 ? 's' : ''}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ComposePage;
